import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import {
	AppContextConfigService,
	AppContextSelector,
	CoreState,
	MarketSelector,
	UiInteractionAction,
} from "@obg/ngx.core";
import { CookieService, ignoreNil, LocationService, PlatformService } from "@obg/ngx.global";
import { includes } from "lodash-es";
import { Observable, Subscription } from "rxjs";
import { map, take } from "rxjs/operators";

import { BETA_BADGE_UI_INTERACTION } from "./beta-badge.const";

@Injectable({
	providedIn: "root",
})
export class BetaBadgeService {
	private readonly showBetaLink$ = this.store.select(AppContextSelector.getCountryCode).pipe(
		ignoreNil(),
		take(1),
		map((countryCode) => {
			const disableLinkByMarket = includes(
				this.appContextConfig.getDisabledMarketsForBetaLink(),
				countryCode.toLowerCase()
			);
			const showBetaLink =
				this.appContextConfig.showBetaLink() &&
				!disableLinkByMarket &&
				(!!this.cookieService.get("OBG-TEST") || !!this.cookieService.get("OBG-BETA"));
			const useAlternative =
				!!this.cookieService.get("OBG-TEST") && this.appContextConfig.getIsAlternativeBetaBadgeLocation();
			return [showBetaLink, useAlternative];
		})
	);

	constructor(
		private cookieService: CookieService,
		private locationService: LocationService,
		private uiInteractionAction: UiInteractionAction,
		private store: Store<CoreState>,
		private marketSelector: MarketSelector,
		private appContextConfig: AppContextConfigService,
		private platformService: PlatformService
	) {}

	showBetaLinkHeader$(): Observable<boolean> {
		return this.showBetaLink$.pipe(
			map(([showBetaLink, useAlternative]) => this.platformService.isBrowser && showBetaLink && !useAlternative)
		);
	}

	showBetaLinkAlternative$(): Observable<boolean> {
		return this.showBetaLink$.pipe(
			map(([showBetaLink, useAlternative]) => this.platformService.isBrowser && showBetaLink && useAlternative)
		);
	}

	goBackToOldSite(): Subscription {
		this.store.dispatch(this.uiInteractionAction.emit(BETA_BADGE_UI_INTERACTION));
		return this.store.select(this.marketSelector.getCurrent()).subscribe((marketCode) => {
			const domain = this.parseUrl(this.locationService.getOrigin());
			this.cookieService.delete("OBG-BETA", { domain });
			const redirectUrl = this.appContextConfig
				.getRedirectUrl()
				.replace("{{domain}}", domain)
				.replace("{{marketCode}}", marketCode.slug);
			this.locationService.navigate(redirectUrl);
		});
	}

	private parseUrl(url: string): string {
		const fragments = url.split(".");
		const listAmount = fragments.length;
		return listAmount > 2 ? `${fragments[listAmount - 2]}.${fragments[listAmount - 1]}` : url;
	}
}
